import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// Types
import Reducers from "types/Reducers";
// Async
import { getQuiz } from './quizAsync';

import jp from 'jsonpath';
import i18n from "i18n";
import IQuestion from "models/Question";
import IBucket from "models/Bucket";
import QuizTypes from "types/QuizTypes";
import IPostQuizPage from "models/PostQuizPage";

const keys = [
  'brand', 'displayName', 'landingPage.callToAction.buttonTitle', 'landingPage.callToAction.title1',
  'landingPage.callToAction.title2', 'landingPage.callToAction.title3', 'landingPage.callToAction.text',
  'proceedText', 'postQuizHeaderText','publicLeaderboard.headerText', 'publicLeaderboard.quizQRCodeTitle',
  'registrationPage.caption', 'waiverText'
];
const initialConsents:string[] = [];

interface IState {
  quiz: any;
  questions: any[];
  interstitialImages: any[];
  preQuizQuestionsAnswers: any;
};

const initialState:IState = {
  quiz: null,
  questions: [],
  interstitialImages: [],
  preQuizQuestionsAnswers: null
};

const slice = createSlice({
  name: Reducers.Quiz,
  initialState,
  reducers: {
    setQuiz: (state, action:PayloadAction<any>) => {
      const quiz = action.payload;

      let questions = quiz.questions;
      let interstitialImages = [];

      if ( quiz.randomizeQuestionOrder ){
        questions = questions.sort(() => Math.random() - 0.5);
      }
      if ( quiz.questionLimit && quiz.questionLimit > 0 ){
        questions = questions.slice(0, quiz.questionLimit);
      }
      if ( quiz.interstitialImageUrl ) interstitialImages.push(quiz.interstitialImageUrl);
      if ( quiz.interstitialImageUrl2 ) interstitialImages.push(quiz.interstitialImageUrl2)

      state.quiz = quiz;
      state.questions = questions;
      state.interstitialImages = interstitialImages;
    },
    updateQuizTranslations: (state, action:PayloadAction<string>) => {
      const quiz = {...state.quiz};
      const lng = action.payload;
      const defaultLanguage = quiz.language;

      quiz.consents = initialConsents;

      if (!quiz.translations[lng]) quiz.translations[lng] = {}

      keys.forEach(key => {
        jp.apply(quiz, `$.${key}`, () => quiz.translations[lng][key] || quiz.translations[defaultLanguage][key])
      })

      quiz.spotPrizes = quiz.translations[lng].spotPrizes ? [quiz.translations[lng].spotPrizes] : [quiz.translations[defaultLanguage].spotPrizes]

      quiz.prizes = quiz.prizes?.map((prize:string, index:number) => {
        const key = `prizes.${index}.prize`;
        return quiz.translations[lng][key] || quiz.translations[defaultLanguage][key]
      })

      quiz.questions.forEach((question:IQuestion) => {
        const questionKey = `questions.${question.ref}.title`;
        const questionPath = `$.questions[?(@.ref == "${question.ref}")].title`;

        jp.apply(quiz, questionPath, () => quiz.translations[lng][questionKey] || quiz.translations[defaultLanguage][questionKey])

        question.answers.forEach(answer => {
          const answerKey = `questions[${question.ref}].answers.${answer.ref}.title`;
          const answerPath = `$.questions[?(@.ref == "${question.ref}")].answers[?(@.ref == "${answer.ref}")].title`;
  
          jp.apply(quiz, answerPath, () => quiz.translations[lng][answerKey] || quiz.translations[defaultLanguage][answerKey])
        })
      })

      if (!!quiz.preQuizPages) {
        quiz.preQuizPages[0]?.questions.forEach((question:IQuestion) => {
          const questionKey = `preQuizPages[0].questions.${question.ref}.title`;
          const questionPath = `$.preQuizPages[0].questions[?(@.ref == "${question.ref}")].title`;
  
          jp.apply(quiz, questionPath, () => quiz.translations[lng][questionKey] || quiz.translations[defaultLanguage][questionKey])
  
          question.answers.forEach(answer => {
            const answerKey = `preQuizPages[0].questions[${question.ref}].answers.${answer.ref}.title`;
            const answerPath = `$.preQuizPages[0].questions[?(@.ref == "${question.ref}")].answers[?(@.ref == "${answer.ref}")].title`;
    
            jp.apply(quiz, answerPath, () => quiz.translations[lng][answerKey] || quiz.translations[defaultLanguage][answerKey])
          })
        })
      }
      

      if ([QuizTypes.Scoring, QuizTypes.Prediction].includes(quiz.type) && !!quiz.postQuizPages) {
        quiz.postQuizPages[0]?.questions.forEach((question:IQuestion) => {
          const questionKey = `postQuizPages[0].questions.${question.ref}.title`;
          const questionPath = `$.postQuizPages[0].questions[?(@.ref == "${question.ref}")].title`;
  
          jp.apply(quiz, questionPath, () => quiz.translations[lng][questionKey] || quiz.translations[defaultLanguage][questionKey])
  
          question.answers.forEach(answer => {
            const answerKey = `postQuizPages[0].questions[${question.ref}].answers.${answer.ref}.title`;
            const answerPath = `$.postQuizPages[0].questions[?(@.ref == "${question.ref}")].answers[?(@.ref == "${answer.ref}")].title`;
    
            jp.apply(quiz, answerPath, () => quiz.translations[lng][answerKey] || quiz.translations[defaultLanguage][answerKey])
          })
        })
      }

      if (quiz.type === QuizTypes.Personality) {
        quiz.postQuizPages?.forEach((postQuizPage:IPostQuizPage, index:number) => {
          const buttonTitleKey = `postQuizPages[${index}].callToAction.buttonTitle`;
          const textKey = `postQuizPages[${index}].callToAction.text`;

          jp.apply(quiz, `$.${buttonTitleKey}`, () => quiz.translations[lng][buttonTitleKey] || quiz.translations[defaultLanguage][buttonTitleKey])
          jp.apply(quiz, `$.${textKey}`, () => quiz.translations[lng][textKey] || quiz.translations[defaultLanguage][textKey])
        })
      }

      if (lng !== quiz.language) {
        quiz.consents = initialConsents.map((consentId:string) => {
          const key = `consents.${consentId}.consent`;
          return quiz.translations[lng][key] || consentId;
        })
      }

      quiz.buckets?.forEach((bucket:IBucket) => {
        const questionKey = `buckets[${bucket.ref}].title`;
        const questionPath = `$.buckets[?(@.ref == "${bucket.ref}")].title`;

        jp.apply(quiz, questionPath, () => quiz.translations[lng][questionKey] || quiz.translations[defaultLanguage][questionKey])
      })

      state.quiz = quiz;
      state.questions = quiz.questions;
    },
    setPreQuizQuestionsAnswers: (state: IState, action) => {
      state.preQuizQuestionsAnswers = action.payload;
    }
  },
  extraReducers(builder) {
    // Get quiz
    builder.addCase(getQuiz.pending, (state) => {
      state.quiz = null;
      state.questions = []
    });
    builder.addCase(getQuiz.fulfilled, (state, action:PayloadAction<any>) => {
      const quiz = action.payload;

      quiz.translations = {
        ...(quiz.translations || {}),
        [quiz.language]: {}
      };

      keys.forEach(key => {
        quiz.translations[quiz.language] = {
          ...quiz.translations[quiz.language],
          [key]: jp.query(quiz, `$.${key}`)[0]
        }
      })

      quiz.translations[quiz.language] = {
        ...quiz.translations[quiz.language],
        spotPrizes: quiz.spotPrizes?.[0]
      }

      quiz.prizes?.forEach((prize: string, index: number) => {  
        quiz.translations[quiz.language] = {
          ...quiz.translations[quiz.language],
          [`prizes.${index}.prize`]: prize
        }
      })

      let questions = quiz.questions;

      questions.forEach((question: IQuestion) => {  
        quiz.translations[quiz.language] = {
          ...quiz.translations[quiz.language],
          [`questions.${question.ref}.title`]: question.title
        }

        question.answers.forEach(answer => {
          quiz.translations[quiz.language] = {
            ...quiz.translations[quiz.language],
            [`questions[${question.ref}].answers.${answer.ref}.title`]: answer.title
          }
        })
      })

      if (!!quiz.preQuizPage) {
        quiz.preQuizPage[0]?.questions.forEach((question: IQuestion) => {  
          quiz.translations[quiz.language] = {
            ...quiz.translations[quiz.language],
            [`preQuizPages[0].questions.${question.ref}.title`]: question.title
          }
  
          question.answers.forEach(answer => {
            quiz.translations[quiz.language] = {
              ...quiz.translations[quiz.language],
              [`preQuizPages[0].questions[${question.ref}].answers.${answer.ref}.title`]: answer.title
            }
          })
        })
      }

      if ([QuizTypes.Scoring, QuizTypes.Prediction].includes(quiz.type) && !!quiz.postQuizPages) {
        quiz.postQuizPages[0]?.questions.forEach((question: IQuestion) => {  
          quiz.translations[quiz.language] = {
            ...quiz.translations[quiz.language],
            [`postQuizPages[0].questions.${question.ref}.title`]: question.title
          }
  
          question.answers.forEach(answer => {
            quiz.translations[quiz.language] = {
              ...quiz.translations[quiz.language],
              [`postQuizPages[0].questions[${question.ref}].answers.${answer.ref}.title`]: answer.title
            }
          })
        })
      }
      
      if (quiz.type === QuizTypes.Personality) {
        quiz.postQuizPages?.forEach((postQuizPage:IPostQuizPage, index: number) => {
          const buttonTitleKey = `postQuizPages[${index}].callToAction.buttonTitle`;
          const textKey = `postQuizPages[${index}].callToAction.text`;

          quiz.translations[quiz.language] = {
            ...quiz.translations[quiz.language],
            [buttonTitleKey]: postQuizPage.callToAction.buttonTitle,
            [textKey]: postQuizPage.callToAction.text,
          }
        })
      }

      quiz.consents?.forEach((consentId:string) => {
        initialConsents.push(consentId)
      })

      quiz.buckets?.forEach((bucket:IBucket) => {  
        quiz.translations[quiz.language] = {
          ...quiz.translations[quiz.language],
          [`buckets[${bucket.ref}].title`]: bucket.title
        }
      })

      let interstitialImages = [];

      if ( quiz.randomizeQuestionOrder ){
        questions = questions.sort(() => Math.random() - 0.5);
      }
      if ( quiz.questionLimit && quiz.questionLimit > 0 ){
        questions = questions.slice(0, quiz.questionLimit);
      }
      if ( quiz.interstitialImageUrl ) interstitialImages.push(quiz.interstitialImageUrl);
      if ( quiz.interstitialImageUrl2 ) interstitialImages.push(quiz.interstitialImageUrl2)

      state.quiz = quiz;
      state.questions = questions;
      state.interstitialImages = interstitialImages;

      i18n.changeLanguage(quiz.language);
    });
    builder.addCase(getQuiz.rejected, (state) => {
      state.quiz = undefined;
      state.questions = [];
      state.interstitialImages = [];
    });
  }
});

export const { setQuiz, updateQuizTranslations, setPreQuizQuestionsAnswers } = slice.actions;

export default slice.reducer;
